import { useEffect, useRef, useState } from "react";

interface ProNumberFilterProps {
  /** Current PRO number value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Callback when filter is removed */
  onRemove: () => void;
  /** Whether to open immediately */
  initialOpen?: boolean;
}

/**
 * ProNumberFilter component - Input field for filtering by PRO number
 *
 * Supports partial matching - searches for documents where PRO number
 * contains the entered text (case-insensitive ILIKE).
 *
 * Example PRO number format: "DYL-123456"
 */
export function ProNumberFilter({
  value,
  onChange,
  onRemove,
  initialOpen = false,
}: ProNumberFilterProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // React to initialOpen prop changes (for auto-open on add)
  useEffect(() => {
    if (initialOpen) {
      setIsOpen(true);
    }
  }, [initialOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Apply value on close if changed
        if (inputValue !== value) {
          onChange(inputValue);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, inputValue, value, onChange]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      setIsOpen(false);
      onChange(inputValue);
    } else if (event.key === "Escape") {
      setIsOpen(false);
      setInputValue(value); // Reset to original value
    }
  };

  const displayValue = value || "All";

  return (
    <div ref={containerRef} className="relative">
      {/* Chip button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-1.5 pr-7
          text-xs
          ${isOpen ? "bg-blue-100 dark:bg-blue-900/30" : "bg-white dark:bg-[#232f48]"}
          border border-gray-200 dark:border-[#324467]
          rounded-full
          hover:bg-gray-50 dark:hover:bg-gray-700
          transition-colors duration-150
        `}
      >
        <span className="font-semibold text-gray-600 dark:text-gray-300">PRO #:</span>
        <span className="text-gray-600 dark:text-gray-300">{displayValue}</span>
      </button>

      {/* Remove button */}
      <button
        type="button"
        aria-label="Remove PRO # filter"
        title="Remove PRO # filter"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={`
          absolute right-1.5 top-1/2 -translate-y-1/2 p-0
          text-gray-400 hover:text-gray-600
          dark:text-gray-500 dark:hover:text-gray-300
          transition-colors duration-150
        `}
      >
        <span className="material-symbols-outlined text-sm">close</span>
      </button>

      {/* Dropdown input */}
      {isOpen && (
        <div
          className={`
            absolute left-0 top-full mt-1 z-50
            w-64 p-3
            bg-white dark:bg-[#192233]
            border border-gray-200 dark:border-[#324467]
            rounded-lg shadow-lg
          `}
        >
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            PRO Number (partial match)
          </label>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. DYL-123"
            className={`
              w-full px-3 py-2
              text-sm text-gray-900 dark:text-white
              bg-white dark:bg-[#111722]
              border border-gray-300 dark:border-[#324467]
              rounded-md
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
            `}
          />
          <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            Press Enter to apply, Escape to cancel
          </p>
        </div>
      )}
    </div>
  );
}
